import React, { useState } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Paper, Box, Tooltip, Button, Chip
} from '@mui/material';
import { Link } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import useApiManager from '../../../../utils/Api/apiManager';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationDialog from '../../../ConfirmationDialog/ConfirmationDialog';
import DealsDialog from './DealsDialog';
import { DealStage, DealType } from '../../../../utils/enums';
import { format } from 'date-fns';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const DeleteRow = ({ hover }) => (
    <Box sx={{ position: 'relative', width: '20px', height: '22px' }}>
        {hover && <DeleteIcon />}
    </Box>
);

const Deals = ({ data, fetchDeals, campaignId, dealsCount, fetchCampaignRelatedEntitiesCount }) => {
    const { put } = useApiManager();
    const [hoveredRow, setHoveredRow] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDealsDialog, setOpenDealsDialog] = useState(false);
    const [currentRow, setCurrentRow] = useState('');

    const handleDealsOpenDialog = () => {
        setOpenDealsDialog(true);
    };

    const handleDealsCloseDialog = () => {
        setOpenDealsDialog(false);
    };

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleConfirmAction = () => {
        handleDelete();
        handleCloseDialog();
    };

    const handleDelete = async () => {
        try {
            const unassignedDeal = await put(`/campaigns/${campaignId}/deals/unassign`, {
                dealId: currentRow.id
            });
            fetchDeals();
            fetchCampaignRelatedEntitiesCount();
        } catch (error) {
            console.error('Error unassigning:', error);
        }
    };

    const handleAddDeals = () => {
        handleDealsOpenDialog();
    };

    return (
        <div>
            <Accordion>
                <AccordionSummary
                    expandIcon={<ArrowDropDownIcon />}
                    aria-controls="deals-content"
                    id="deals-header"
                    sx={{ px: 2 }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            padding: '8px'
                        }}
                    >
                        <h4 style={{ display: 'flex', alignItems: 'center', margin: 0, fontWeight: 500 }}>
                            Deals
                            {dealsCount > 0 && (
                                <Tooltip title={`Total Deals: ${dealsCount}`} arrow>
                                    <Chip
                                        label={dealsCount}
                                        size="small"
                                        sx={{
                                            fontWeight: 'normal',
                                            fontFamily: 'Consolas, Menlo, Monaco, monospace',
                                            verticalAlign: 'middle',
                                            marginLeft: 1
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </h4>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent accordion toggle
                                    handleAddDeals();
                                }}
                            >
                                Add New
                            </Button>
                        </Box>
                    </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="deals table">
                            <TableHead>
                                <TableRow>
                                    <TableCell width={10}>-</TableCell>
                                    <TableCell maxWidh={250}>Deal Name</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Stage</TableCell>
                                    <TableCell>Probability (%)</TableCell>
                                    <TableCell>Closing Date</TableCell>
                                    <TableCell>Type</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data?.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        onMouseEnter={() => setHoveredRow(row.id)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <Tooltip title="Delete">
                                            <TableCell
                                                sx={{ width: '20px', height: '22px', padding: '8px', cursor: 'pointer' }}
                                                onClick={() => {
                                                    setCurrentRow(row);
                                                    handleOpenDialog();
                                                }}
                                            >
                                                <DeleteRow hover={hoveredRow === row.id} />
                                            </TableCell>
                                        </Tooltip>
                                        <TableCell component="th" scope="row">
                                            <Link to={`/deals/${row.id}`}>{row.dealName}</Link>
                                        </TableCell>
                                        <TableCell>{row.amount}</TableCell>
                                        <TableCell>{DealStage?.[row.stage]?.label || 'Unknown Stage'}</TableCell>
                                        <TableCell>{row.probability}</TableCell>
                                        <TableCell>
                                            {row.closingDate ? format(new Date(row.closingDate), 'MMMM do yyyy') : 'No closing date'}
                                        </TableCell>
                                        <TableCell>{DealType?.[row.type]?.label || 'Unknown Type'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <ConfirmationDialog
                        open={openDialog}
                        title="Confirm Action"
                        content="Are you sure you want to unassign this deal?"
                        onClose={handleCloseDialog}
                        onConfirm={handleConfirmAction}
                        confirmText="Confirm"
                        cancelText="Cancel"
                    />

                    <DealsDialog
                        open={openDealsDialog}
                        setOpen={setOpenDealsDialog}
                        campaignId={campaignId}
                        fetchDeals={fetchDeals}
                        fetchCampaignRelatedEntitiesCount={fetchCampaignRelatedEntitiesCount}
                    />
                </AccordionDetails>
            </Accordion>

        </div>
    )
}

export default Deals;